import { useFormik } from "formik";
import * as Yup from "yup";
import useValidations from "../../../../utils/hooks/useValidations";
import { toast } from "react-toastify";
import { addOrUpdateCustomOffers, getDataById } from "../api";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { useGetCustomOffersBasicDataQuery } from "../../../../store/queries/User";
import { updateConfig } from "../../../../store/slices/User/CustomOffers/customOfferSlice";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";

const useCreateOffer = (refetch, closeModal) => {
  const [isType, setType] = useState(null);
  const [isCustomOfferCreate, setIsCustomOfferCreate] = useState(false);

  const { englishRegex } = useValidations();
  const { isEdit, isLoading, selectedId, customOfferListEditData } =
    useSelector((state) => state.customOffers);
  const { data: offersBasicData = {} } = useGetCustomOffersBasicDataQuery();
  const dispatch = useDispatch();
  // const timeFormat = "hh:mm A";

  useEffect(() => {
    if (selectedId) {
      getDataById(selectedId).then((response) => {
        if (response?.data?.status_code === 200) {
          dispatch(
            updateConfig((state) => {
              state.customOfferListEditData = response?.data?.data;
            })
          );
        } else if (response?.data?.error_code === 403) {
          formik.setErrors(response?.data?.errors);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (customOfferListEditData !== "" && isEdit) {
      Object.keys(customOfferListEditData || {}).forEach((key) => {
        let value = customOfferListEditData?.[key];

        // Convert from_hour and to_hour strings to dayjs objects
        if (
          ["from_hour", "to_hour"].includes(key) &&
          typeof value === "string"
        ) {
          value = dayjs(value, "hh:mm A");
        }

        formik.setFieldValue(key, value);
      });
    }
  }, [customOfferListEditData]);

  const validation = Yup.object({
    name: Yup.string()
      .trim()
      .matches(englishRegex, `Enter valid offer name`)
      .max(20, "Offer name must be at most 20 characters")
      .required(`Enter offer name`),
    offer_type: Yup.string().required("Select type"),
    offer_period: Yup.string().required("Select period"),
    to_hour: Yup.mixed()
      .nullable()
      .test("is-valid-or-null", "Invalid time", function (value) {
        const { from_hour } = this.parent;
        if (!from_hour || value === null) return true; // Skip if from_hour is not set
        return dayjs.isDayjs(value); // Ensure to_hour is dayjs if set
      })
      .test("required-if-from-hour", "To hour is required", function (to_hour) {
        const { from_hour } = this.parent;
        // Only require to_hour if from_hour is set
        if (from_hour && (to_hour === null || to_hour === undefined)) {
          return false; // to_hour is required in this case
        }
        return true; // Not required otherwise
      })
      .test(
        "is-after-from-hour",
        "To hour must be at least 1 minute after From hour",
        function (to_hour) {
          const { from_hour } = this.parent;

          if (!dayjs.isDayjs(from_hour) || !dayjs.isDayjs(to_hour)) {
            return true; // Skip test if invalid
          }

          return to_hour.diff(from_hour, "minute") >= 1;
        }
      ),
    splited_discount: Yup.string().required("Select splited discount"),
    award_loyalty_point: Yup.number().required("Select award loyalty point"),
    offer_limit: Yup.string().required("Please enter offer limit"),
    min_cart_amount: Yup.string().required("Please enter minimum cart amount"),
    max_cart_amount: Yup.string()
      .required("Please enter maximum cart amount")
      .when("min_cart_amount", (min_cart_amount, schema) =>
        schema.test({
          name: "maxGreaterThanMin",
          message:
            "Maximum cart amount must be greater than minimum cart amount",
          test: function (value) {
            if (!value || !min_cart_amount) return true; // skip test if either is empty
            return Number(value) > Number(min_cart_amount);
          },
        })
      ),
    bin_number_limit: Yup.number()
    .required("Please select bin series"),
    bin_start: Yup.string()
    .test({
      name: "binLimitRequiredBeforeStart",
      message: "Please select bin series first",
      test: function (value) {
        const { bin_number_limit } = this.parent;
        return !!bin_number_limit;
      },
    })
    .when("bin_number_limit", (limit, schema) => {
      if (!limit) return schema;
      return schema
        .required("Please enter bin start")
        .test({
          name: "binStartLength",
          message: `Bin start must be ${limit} digits`,
          test: function (value) {
            if (!value) return true;
            return value.length === Number(limit);
          },
        });
    }),

  bin_end: Yup.string()
    .test({
      name: "binLimitRequiredBeforeEnd",
      message: "Please select bin series first",
      test: function (value) {
        const { bin_number_limit } = this.parent;
        return !!bin_number_limit;
      },
    })
    .when(["bin_start", "bin_number_limit"], ([bin_start, limit], schema) => {
      if (!limit) return schema;
      return schema
        .required("Please enter bin end")
        .test({
          name: "maxBinEnd",
          message: "Bin end must be greater than or equal to bin start",
          test: function (value) {
            if (!value || !bin_start) return true;
            return Number(value) >= Number(bin_start);
          },
        })
        .test({
          name: "binEndLength",
          message: `Bin end must be ${limit} digits`,
          test: function (value) {
            if (!value) return true;
            return value.length === Number(limit);
          },
        });
    }),
    apple_bin_number_limit: Yup.number()
    .required("Please select apple pay bin"),
    apple_pay_bin_start: Yup.string()
    .test({
      name: "appleBinLimitRequiredBeforeStart",
      message: "Please select apple apple pay bin first",
      test: function (value) {
        const { apple_bin_number_limit } = this.parent;
        return !!apple_bin_number_limit;
      },
    })
    .when("apple_bin_number_limit", (limit, schema) => {
      if (!limit) return schema;
      return schema
        .required("Please enter apple pay bin start")
        .test({
          name: "appleBinStartLength",
          message: `Apple bin start must be ${limit} digits`,
          test: function (value) {
            if (!value) return true;
            return value.length === Number(limit);
          },
        });
    }),

  apple_pay_bin_end: Yup.string()
    .test({
      name: "appleBinLimitRequiredBeforeEnd",
      message: "Please select apple apple pay bin first",
      test: function (value) {
        const { apple_bin_number_limit } = this.parent;
        return !!apple_bin_number_limit;
      },
    })
    .when(["apple_pay_bin_start", "apple_bin_number_limit"], ([start, limit], schema) => {
      if (!limit) return schema;
      return schema
        .required("Please enter apple pay bin end")
        .test({
          name: "maxAppleBinEnd",
          message: "Apple bin end must be greater than or equal to bin start",
          test: function (value) {
            if (!value || !start) return true;
            return Number(value) >= Number(start);
          },
        })
        .test({
          name: "appleBinEndLength",
          message: `Apple bin end must be ${limit} digits`,
          test: function (value) {
            if (!value) return true;
            return value.length === Number(limit);
          },
        });
    }),
    discount: Yup.string()
      .required("Please enter discount")
      .test("is-valid-discount", "Discount cannot exceed 100", (value) => {
        const num = Number(value);
        return !isNaN(num) && num <= 100;
      }),
    start_date: isType === 1 && Yup.string().required("Please enter from date"),
    end_date: isType === 1 && Yup.string().required("Please enter to date"),
   transaction_limit: Yup.mixed().when('transaction_period', {
  is: (val) => !!val,
  then: () =>
    Yup.number()
      .transform((value, originalValue) => originalValue === '' ? undefined : Number(originalValue))
      .required('Please enter transaction limit')
      .moreThan(0, 'Transaction limit must be greater than 0')
      .max(10000, 'Transaction limit must be less than or equal to 10000'),
  otherwise: () =>
    Yup.number()
      .transform((value, originalValue) => originalValue === '' ? undefined : Number(originalValue))
      .notRequired(),
   }),
    applicable_variant: Yup.array().nullable(),

    product_id: Yup.array().nullable(),

    inclusion_type: Yup.string()
      .nullable()
      .test(
        "required-based-on-product",
        "Select Applicable Type",
        function (value) {
          const { product_id } = this.parent;

          // If product_id has 1 or more selected items → require inclusion_type
          if (Array.isArray(product_id) && product_id.length > 0) {
            return Boolean(value && value.trim() !== "");
          }

          // If no products selected → no validation needed
          return true;
        }
      ),
  });

  const transformFormValues = (values) => {
    return Object.keys(values).reduce((acc, key) => {
      if (
        [
          "offer_limit",
          "min_cart_amount",
          "max_cart_amount",
          "bin_start",
          "bin_end",
          "apple_pay_bin_start",
          "apple_pay_bin_end",
          "discount",
          "transaction_limit"
        ].includes(key) &&
        values[key] !== "" &&
        values[key] !== null
      ) {
        // Check if the value can be parsed as a float (decimal number)
        const parsedValue = parseFloat(values[key]);

        // If the value is a valid number (including decimals), store it
        if (!isNaN(parsedValue)) {
          acc[key] = parsedValue;
        } else {
          acc[key] = values[key]; // If it's not a valid number, leave it as it is
        }
      } else {
        acc[key] = values[key]; // Leave string values as they are
      }
      return acc;
    }, {});
  };
  const splitedDiscountOptions = [
  { id: 1, name: "Yes"},
  { id: 2, name: "No"},
];

  const formik = useFormik({
    initialValues: {
      name: "",
      start_date: "",
      end_date: "",
      offer_type: "",
      offer_period: "",
      category_id: [],
      product_id: [],
      customer_key: [],
      offer_day: [],
      from_hour: null,
      to_hour: null,
      time_filter: 2,
      splited_discount: "",
      award_loyalty_point: 1,
      offer_limit: "",
      min_cart_amount: "",
      max_cart_amount: "",
      bin_number_limit: "",
      bin_start: "",
      bin_end: "",
      apple_bin_number_limit: "",
      apple_pay_bin_start: "",
      apple_pay_bin_end: "",
      discount: "",
      transaction_period: "",
      transaction_limit: "",
      status: "" ?? 2,
      inclusion_type: "",
      applicable_variant: "",
    },
    validationSchema: validation,
    onSubmit: (values) => {
      setIsCustomOfferCreate(true);
      const formattedFromHour = values.from_hour
        ? values.from_hour.format("hh:mm A")
        : null;
      const formattedToHour = values.to_hour
        ? values.to_hour.format("hh:mm A")
        : null;
      const submitData = {
        ...values,
        from_hour: formattedFromHour,
        to_hour: formattedToHour,
      };
      // Step 1: Modify the values if needed (e.g., clear start_date and end_date)
      const rawValues =
        values.offer_type === "UNLIMITED"
          ? { ...submitData, start_date: "", end_date: "" }
          : submitData;

      // Step 2: Transform the values, converting integer fields
      const transformedValues = transformFormValues(rawValues);

      const createData = {
        ...transformedValues,
        status: !values?.status ? 2 : values?.status, // Default to 2 if no status is provided
      };

      const params = {
        offer_id: selectedId,
        ...createData,
      };

      const data = isEdit ? params : createData;

      addOrUpdateCustomOffers(data).then((response) => {
        if (response?.data?.status_code === 200) {
          toast.success(
            isEdit ? `Offer updated successfully` : `Offer added successfully`
          );
          setIsCustomOfferCreate(false);
          refetch?.();
          closeModal();
        } else if (
          response?.data?.status_code === 422 ||
          response?.data?.errors
        ) {
          Object.keys(response?.data?.errors).forEach((field) => {
            formik.setFieldError(field, response?.data?.errors[field]);
          });
          setIsCustomOfferCreate(false);
        } else {
          toast.error(`Something went wrong!`);
          setIsCustomOfferCreate(false);
          closeModal();
        }
      });
    },
  });

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    } else {
      return "";
    }
  };
  const handleTypeChange = (e) => {
    formik?.setFieldValue("offer_type", e?.id || null);
    setType(e?.id);
  };
  const handlePeriodChange = (e) => {
    formik?.setFieldValue("offer_period", e?.id || null);
  };
  const handleTransactionPeriodChange = (e) => {
    formik?.setFieldValue("transaction_period", e?.id || null);
  };

  const getMinEndDate = () => {
    const startDate = formik?.values?.start_date;
    if (!startDate) return "";

    const date = new Date(startDate);
    date.setDate(date.getDate()); // Add one day

    return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };
  const getMaxDate = () => {
    const endDate = formik?.values?.end_date;
    if (!endDate) return "";

    const date = new Date(endDate);
    date.setDate(date.getDate());

    return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const selectedCategoryIds = formik?.values?.category_id || [];
  const selectedProductIds = formik?.values?.product_id || [];

  const allProducts = offersBasicData?.data?.product || [];

  const handleCategoryChange = (selected) => {
    const selectedIds = selected?.map((opt) => opt._id) || [];
    formik.setFieldValue("category_id", selectedIds);

    // Find removed categories
    const removedCategoryIds = selectedCategoryIds.filter(
      (prevId) => !selectedIds.includes(prevId)
    );

    // Products that belong to selected categories
    const categoryProducts = allProducts.filter((product) =>
      selectedIds.includes(product.parent_id)
    );

    // Already selected product objects
    const selectedProductObjects = allProducts.filter((product) =>
      selectedProductIds.includes(product._id)
    );

    // Combine and remove duplicates using a Map by _id
    const combinedProductsMap = new Map();
    [...categoryProducts, ...selectedProductObjects].forEach((product) => {
      combinedProductsMap.set(product._id, product);
    });
    const combinedProducts = Array.from(combinedProductsMap.values());

    // Remove products related to removed categories
    const finalProducts = combinedProducts.filter(
      (product) => !removedCategoryIds.includes(product.parent_id)
    );

    // Update state and formik
    formik.setFieldValue(
      "product_id",
      finalProducts.map((opt) => opt._id)
    );
  };

  useEffect(() => {
    if (!formik.values.from_hour) {
      formik.setFieldValue("to_hour", null);
    }
  }, [formik.values.from_hour]);

  return {
    formik,
    isLoading,
    isEdit,
    isCustomOfferCreate,
    getFieldError,
    offersBasicData,
    handleTypeChange,
    handlePeriodChange,
    getMinEndDate,
    getMaxDate,
    handleCategoryChange,
    handleTransactionPeriodChange,
    splitedDiscountOptions,
  };
};

export default useCreateOffer;
